import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { Map as LeafletMap } from 'leaflet';
import styled from '@emotion/styled';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GeoJsonData = any;
import { theme, scaled } from './styles/theme';
import { LoadingOverlay, Spinner, ErrorMessage, ToggleGroup, ToggleButton } from './styles/components';
import { fetchMeta, fetchWahlkreise, fetchClusterAnnouncements, trackPageview, getApiBase } from './utils/api';
import { injectDatasetJsonLd, resolveCanonicalPageUrl } from './utils/structuredData';
import {
  decodeUrlState,
  encodeUrlState,
  isFullDatasetView,
  mergeOwnParams,
  toSearchString,
  type UrlState,
} from './utils/urlState';
import { formatDateLong } from './utils/formatters';
import { getLocale, industryLabel, otherLabel, useT } from './i18n';
import germanyStatesGeoJson from './germany-states.json';
import { deriveTierState, toggleTier, DEFAULT_INVESTMENT_TYPES } from './utils/investmentTiers';
import { PHYSICAL_TYPES, FINANCIAL_TYPES } from './utils/colors';
import { useChartData } from './hooks/useChartData';
import { useTablePage } from './hooks/useTablePage';
import { FilterPanel } from './components/FilterPanel';
import { ActiveFilterChips } from './components/ActiveFilterChips';
import { RankedBarChart } from './components/RankedBarChart';
import type { RankedDatum } from './components/RankedBarChart';
import { TimeSeriesChart } from './components/TimeSeriesChart';
import { InvestmentMap, GERMANY_BOUNDS } from './components/InvestmentMap';
import { InvestmentTable } from './components/InvestmentTable';
import { SearchInput } from './components/SearchInput';
import { AnnouncementModal } from './components/AnnouncementModal';
import { ExportDialog } from './components/ExportDialog';
import { FeedbackDialog } from './components/FeedbackDialog';
import { Spotlights, type Spotlight } from './components/Spotlights';
import { LatestNews } from './components/LatestNews';
import type { FilterState, MetaData, TableSort, WahlkreiseGeoJson, MapPoint, Announcement, HierarchicalFilterOption } from './types';

const PAGE_SIZE = 50;

// `dz-investments-widget-root color-data` are the scoping anchors for the
// embedded widget. `.color-data` (shipped by dz-shared-styles tokens.scss)
// sets `--primary-color: var(--color-data)`; we also hard-set
// `--primary-color-hover` here so button/dropdown :hover states still
// resolve when the host page only loads the base tokens.
//
// --dz-font-base is the widget's own type base, and the only place the
// design's 16px assumption is written down. Every size in the widget goes
// through `scaled()` (styles/theme.ts), which multiplies this variable —
// so the widget renders at design size whatever root the host page uses.
//
// It exists because `font-size: 16px` here does NOT do that job, contrary to
// what this comment used to claim: `rem` resolves against the document root,
// never against an ancestor, and the host sets `html { font-size: 13px }`.
// Measured on the live page, the widget's 14px text rendered at 11.4px and
// the map legend at 8.9px. The declaration stays because `em` values (button
// paddings, the select chevron) and anything inheriting a bare font-size do
// key off this element.
const Container = styled.div`
  /* Full-bleed: the widget spans however wide the host gives it, with no
     width cap and no centering margins — content reaches both boundaries. */
  width: 100%;
  --dz-font-base: 16px;
  font-size: 16px;
  /* Match budget's widget root: paint the page background ourselves so
     flat Section areas (the table, header rows) sit on the same beige
     surface as the carded chart/map/filter panel instead of showing the
     host page through. */
  background: var(--color-bg-surface);
  --primary-color-hover: var(--color-data-700);

  /* Strip borders from every button in the widget: the shared .button--outline
     rule (1px solid black via --border) on the tier toggle + pagination, and
     the browser's native button border on the .tab-buttons toggle segments
     (the shared styles never reset it). Dropdown/input borders are untouched.
     The two-class selectors outrank the shared single-class rules. */
  & .button,
  & .tab-buttons__button { border: none; }

  /* The page runs edge-to-edge at every width: no side gutters, content sits
     flush against the widget boundary. The Spotlights rail bleeds its card
     shadows LEFT past that boundary (see Spotlights.tsx) — left overhang is
     never scrollable, so it can't push the page wider than the viewport. */
  @media (max-width: 1024px) {
    /* Read by the shared Card (styles/components) to shrink its inner padding
       on mobile; the chart/filter cards were doubling the gutter (24+24). */
    --dz-card-pad: var(--space-5);
  }
`;

// Boot placeholder. The spinner sat in a 400px box that then became the whole
// dashboard — the largest single layout shift on the host page (the audit
// measured CLS 0.342).
//
// The numbers are MEASURED settled heights of the real widget, minus a margin,
// never estimates. Re-measured with headless Edge against this build served
// into the live page, because scaled() made every box ~23% taller (table rows
// 55 → 61px):
//
//   2530px → 2036   1890px → 2036   1570px → 2080   1440px → 2098
//   1250px → 2177   1070px → 2213   1025px → 2213
//   | 1010px → 2865    970px → 2865    738px → 2955
//   | 601px → 3088  |  600px → 12981   570px → 13017   390px → 14210
//
// Two boundaries show up: the widget's own 1024px breakpoint, and 600px, where
// InvestmentTable swaps its rows for stacked cards and the widget becomes four
// times taller.
//
// The floor is NOT the smallest of those, though, because a first render is no
// longer always the full dataset: ?spotlight= and ?search= links (and the ones
// the SEO summary emits) open a filtered view, which settles shorter. Measured
// on a single-result search — the shortest first render there is:
//
//   1440px → 1596    700px → 2398    390px → 2816
//
// Reserving MORE than the settled height would not remove a shift, it would
// create a new one in the opposite direction when the box shrinks — and on a
// filtered link that shrink would be the size of the whole dataset's table.
//
// So the box asks which of the two it is. isFullDatasetView() reads the boot
// URL (already parsed, one render before this box paints), and each band gets
// the floor for its case: the full-dataset heights above, the single-result
// ones below. The 600px band is where this pays: ~13000px reserved for the
// default view instead of 2300, and still 2300 for a filtered link that would
// otherwise have been overshot by 10000.
const LoadingContainer = styled(Container)<{ $fullDataset: boolean }>`
  min-height: ${p => (p.$fullDataset ? '2000px' : '1500px')};

  @media (max-width: 1024px) {
    min-height: ${p => (p.$fullDataset ? '2800px' : '2300px')};
  }

  /* Below 600px InvestmentTable swaps rows for stacked cards. */
  @media (max-width: 600px) {
    min-height: ${p => (p.$fullDataset ? '12500px' : '2300px')};
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  /* minmax(0, 1fr), not plain 1fr: a bare 1fr track's min is auto, so the
     table's min-width (1420px on narrow embeds) would propagate up and widen
     the track — stretching every row, filter card included, past the
     container. A zero track minimum keeps rows at container width and lets
     the table scroll inside its own overflow-x container instead. */
  grid-template-columns: minmax(0, 1fr);
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const FooterRemark = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
`;

// Publication metadata (first published / last update). Sits at the top of the
// dashboard, directly above the news band — sans for dense factual text, which
// matches the small caption treatment used elsewhere on the website (data
// dashboards aren't body prose; they're tabular).
const PublicationDates = styled.p`
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  line-height: 1.2;
  color: ${theme.colors.text.muted};
  text-align: left;
  margin: 0;
`;

// Dense disclaimer block — smaller and looser than the date/contact captions,
// matching the budget widget's FooterText treatment.
// Leads the footer now that the dates sit at the top of the dashboard, so no
// top margin — FooterRemark's padding already spaces it off the border.
const FooterText = styled.p`
  font-family: var(--sans-stack);
  font-size: ${scaled(12)};
  line-height: 1.5;
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const FooterContact = styled.p`
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  line-height: 1.2;
  color: ${theme.colors.text.muted};
  text-align: left;
  margin: ${theme.spacing.sm} 0 0 0;

  a {
    color: inherit;
  }
`;

// Link-styled button in the footer opening the new-project suggestion form.
const FooterSuggestButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:hover { color: ${theme.colors.text.primary}; }
`;

type ChartView = 'industries' | 'countries' | 'timeseries';

const ChartViewToggle = styled.div`
  display: grid;
  /* Two equal halves so "Gruppierung" starts over the map column, as it did
     before the tier toggle joined this row. minmax(0, …) keeps a wide left
     cell from stretching the track past the container — its content wraps
     inside the cell instead (see ToggleRowGroup). */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  /* Bottom-align so the two button rows line up even if the labels differ in
     height — the buttons are each group's last element and share one height. */
  align-items: end;
  gap: ${theme.spacing.sm};

  @media (max-width: 1024px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// Left cell of the toggle row: the tier toggle (Investitionsprojekte /
// Finanztransaktionen) sits left of the "Visualisierung:" group, split by a
// vertical rule. Wraps so a narrow column drops the second group onto its own
// line rather than overflowing.
const ToggleRowGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  /* Shrinkable so a narrow embed wraps inside this group instead of pushing
     the row wider than the container. */
  min-width: 0;

  /* The two groups need ≈680px together — more than this cell gets below a
     ~1370px viewport, where they have to break onto two lines. Stack them
     deliberately there (rule turns horizontal) rather than letting them
     free-wrap, which would leave the vertical rule dangling mid-row. */
  @media (max-width: 1370px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

// Hairline separating the tier toggle from the visualisation toggle. Vertical
// while the groups sit side by side; once the cell is too narrow for that and
// they stack, it turns into the horizontal rule between them.
const ToggleDivider = styled.span`
  align-self: stretch;
  width: 1px;
  background: ${theme.colors.border};

  @media (max-width: 1370px) {
    width: auto;
    height: 1px;
  }
`;

const BubbleModeGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: ${theme.spacing.sm};
  /* Caption ("Visualisierung", "Gruppierung") styled like the filter field
     labels — sans, near-black — so it reads as a description beside the pills
     rather than blending into the buttons. Medium weight nudges the emphasis. */
  font-family: ${theme.fontFamilySans};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.text.primary};

  /* On phones the caption + three-segment toggle don't fit on one line, so
     the toggle group spills past the viewport. Stack the caption above the
     pills and let them take the full row instead. */
  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing.xs};
  }
`;

// Label sits to the left of the toggle group, sharing its text baseline.
// nowrap keeps it on one line so the pills don't wrap under it.
const ToggleLabel = styled.span`
  white-space: nowrap;
`;

const ChartAndMapRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.lg};
  min-height: 500px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

// The "Gruppierung" (map bubble mode) toggle controls the map, but on desktop
// it reads best in the toggle row above, aligned over the map column. On mobile
// the chart and map stack, so that row position lands it above the *chart* —
// far from what it controls. We render the same toggle in two slots and let CSS
// pick one: desktop keeps it in ChartViewToggle (display:contents → behaves
// exactly as a direct grid child, so the desktop layout is unchanged), mobile
// drops it into ChartAndMapRow between the chart and the map. Only one is ever
// laid out (the other is display:none), so the control never appears twice.
const GruppierungDesktopSlot = styled.div`
  display: contents;
  @media (max-width: 1024px) { display: none; }
`;
const GruppierungMobileSlot = styled.div`
  display: none;
  @media (max-width: 1024px) { display: contents; }
`;

// Sizes the search pill in the projects table's header row. Shrinks on
// narrow embeds; the header row wraps it onto its own line when tight.
const TableSearchSlot = styled.div`
  width: min(280px, 70vw);
`;

const DAY_MS = 86_400_000;

const defaultFilters: FilterState = {
  dateFrom: null,
  dateTo: null,
  federalStates: [],
  investmentTypes: [...DEFAULT_INVESTMENT_TYPES],
  investmentStatuses: [],
  industries: [],
  companies: [],
  search: '',
  spotlight: '',
};

// Fallback header for the bubble-click modal when the cluster can't be fetched
// (no cluster_id, or the request fails). MapPoint lacks status/date, so those
// stay null — the cluster fetch normally supplies a complete announcement.
// Industry drill state. The bar chart starts at L1 (path empty); clicking a
// bar with multiple deeper-level children pushes a step onto the path and the
// chart re-fetches grouped at the next level. Chart level = path.length + 1.
// `leaf` is an active highlight/filter at the current chart level for bars
// that *can't* drill further (0 or 1 child, or L3 leaves, or the "Sonstige"
// long-tail aggregate which doesn't represent a single industry).
type IndustryDrillStep = { level: 1 | 2; label: string };
type IndustryLeaf = { level: 1 | 2 | 3; label: string; originals: string[] };

// Walk the industry tree along `path` (matching each step by level + label)
// and return the children that sit one level below `clickedLabel` — i.e. the
// candidates we'd reveal if the user drilled into that bar.
function getChildrenForClick(
  tree: HierarchicalFilterOption[],
  path: IndustryDrillStep[],
  clickedLabel: string,
): HierarchicalFilterOption[] {
  let nodes: HierarchicalFilterOption[] = tree;
  for (const step of path) {
    const found = nodes.find(n => n.label === step.label && n.level === step.level);
    if (!found || !found.children) return [];
    nodes = found.children;
  }
  const clicked = nodes.find(n => n.label === clickedLabel);
  return clicked?.children ?? [];
}

function announcementFromPoint(p: MapPoint): Announcement {
  return {
    id: p.announcement_id,
    cluster_id: p.cluster_id,
    announced_event_date: null,
    first_announced_date: null,
    investing_company: p.investing_company,
    project_name: p.project_name,
    type_of_investment: p.type_of_investment,
    investment_status: null,
    jobs_created: null,
    investment_volume_value_eur: p.investment_volume_value_eur,
    project_start_date: null,
    project_end_date: null,
    primary_industry: p.primary_industry,
    locations: [{
      id: p.location_id,
      city_name: p.city_name,
      latitude: p.latitude,
      longitude: p.longitude,
      federal_state: p.federal_state,
    }],
  };
}

export interface AppProps {
  /** Same base the API client was configured with; used in the Dataset markup. */
  apiBaseUrl?: string;
  /** Canonical host-page URL for the Dataset markup (see structuredData.ts). */
  pageUrl?: string;
  /**
   * Internal: render() passes one object per container so view choices that
   * are not in the URL (chart view, map mode, tiers, table sort) survive the
   * remount a language switch triggers. Filters and dates come back from the URL.
   */
  viewCarry?: Record<string, unknown>;
}

/** useState whose value is mirrored into `carry` and restored from it on the next mount. */
function useCarried<T>(carry: Record<string, unknown> | undefined, key: string, initial: T) {
  const pair = useState<T>(() => (carry && key in carry ? (carry[key] as T) : initial));
  const value = pair[0];
  useEffect(() => {
    if (carry) carry[key] = value;
  }, [carry, key, value]);
  return pair;
}

export function App({ apiBaseUrl, pageUrl, viewCarry }: AppProps = {}) {
  const m = useT();
  // A shared link's filters, read ONCE before the first render. Deliberately
  // not an effect: an effect would let the first fetch go out with the default
  // filters and a second one with the URL's — the duplicate round this widget
  // just stopped making. Read through a ref so the write effect below can also
  // see what we started from.
  const initialUrl = useRef<UrlState>(
    decodeUrlState(typeof window !== 'undefined' ? window.location.search : ''),
  );

  const [meta, setMeta] = useState<MetaData | null>(null);
  const [filters, setFilters] = useState<FilterState>(() => initialUrl.current.filters);
  const [fromTimestamp, setFromTimestamp] = useState(0);
  const [toTimestamp, setToTimestamp] = useState(0);
  const [chartView, setChartView] = useCarried<ChartView>(viewCarry, 'chartView', 'industries');
  const [bubbleMode, setBubbleMode] = useCarried<'off' | 'cluster' | 'single'>(viewCarry, 'bubbleMode', 'single');
  const [activeTiers, setActiveTiers] = useCarried<{ physical: boolean; financial: boolean }>(viewCarry, 'activeTiers', { physical: true, financial: false });
  const [tableView, setTableView] = useCarried<'physical' | 'financial'>(viewCarry, 'tableView', 'physical');
  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(null);
  // Detail modal opened by a map bubble click (mirrors the table-row modal).
  const [mapModal, setMapModal] = useState<{ announcement: Announcement; entries: Announcement[] } | null>(null);
  const [geoJsonData, setGeoJsonData] = useState<GeoJsonData | null>(null);
  const [wahlkreiseData, setWahlkreiseData] = useState<WahlkreiseGeoJson | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const selectionSourceRef = useRef<'bubble' | 'table' | null>(null);

  const [physicalPage, setPhysicalPage] = useState(0);
  const [physicalSort, setPhysicalSort] = useCarried<TableSort>(viewCarry, 'physicalSort', { field: 'volume', dir: 'desc' });
  const [financialPage, setFinancialPage] = useState(0);
  const [financialSort, setFinancialSort] = useCarried<TableSort>(viewCarry, 'financialSort', { field: 'volume', dir: 'desc' });
  // Filter change counter — incremented on filter/time changes to tell useTablePage to skip findCluster
  const [filterChangeId, setFilterChangeId] = useState(0);
  const [aggregateClusterIds, setAggregateClusterIds] = useState<number[] | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedWahlkreis, setSelectedWahlkreis] = useState<{ wkrNr: number; name: string } | null>(null);
  // Industry drill: path of L1/L2 bars the user has drilled INTO. Empty path
  // = L1 view (default). One step = chart re-fetches at L2 within that L1;
  // two steps = chart at L3 within the deepest L2. `leaf` is a non-drilled
  // bar highlighted at the current chart level (the "0/1 child" fallback,
  // L3 leaves, and "Sonstige"). Both scope the table + map; the chart's
  // grouping is determined by the path alone.
  const [industryDrillPath, setIndustryDrillPath] = useState<IndustryDrillStep[]>([]);
  const [industryLeaf, setIndustryLeaf] = useState<IndustryLeaf | null>(null);
  // CSV export dialog for the projects table (physical tier only).
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showSuggestDialog, setShowSuggestDialog] = useState(false);

  const minTimestamp = useMemo(() => meta ? new Date(meta.dateRange.min).getTime() : 0, [meta]);
  const maxTimestamp = useMemo(() => meta ? new Date(meta.dateRange.max).getTime() : 0, [meta]);

  const tierState = useMemo(() => deriveTierState(filters.investmentTypes), [filters.investmentTypes]);

  // Build effective filters with date range.
  //
  // A bound that equals the data's own bound is NOT sent. That looks like we
  // are dropping a filter the slider visibly shows, so here is why it is a
  // no-op: the API filters on PROJECT_FIRST_DATE = the cluster's
  // MIN(announced_event_date) (db.ts), while /api/meta's dateRange is
  // MIN/MAX(announced_event_date) over the same live rows. Every project's
  // first date therefore lies inside [min, max], and `>= min` / `<= max`
  // exclude nothing. (Locked down by date-semantics.test.ts.)
  //
  // Omitting them is what makes the first render's request the FINAL request.
  // fromTimestamp/toTimestamp are 0 until /api/meta lands, so if we sent them
  // as soon as they were non-zero, every hook below would fire once unbounded
  // and again with the bounds — the duplicate round that cost ~1.1 s before
  // the table settled. Gating the hooks on `meta` would fix the duplication
  // too, but by delaying the first request instead of by removing the second.
  const effectiveFilters = useMemo<FilterState>(() => ({
    ...filters,
    dateFrom: fromTimestamp > 0 && fromTimestamp > minTimestamp
      ? new Date(fromTimestamp).toISOString().split('T')[0]! : null,
    dateTo: toTimestamp > 0 && toTimestamp < maxTimestamp
      ? new Date(toTimestamp).toISOString().split('T')[0]! : null,
  }), [filters, fromTimestamp, toTimestamp, minTimestamp, maxTimestamp]);

  // Intersection-based type filters for each table
  const physicalFilters = useMemo<FilterState>(() => {
    const types = effectiveFilters.investmentTypes.length === 0
      ? [...PHYSICAL_TYPES]
      : effectiveFilters.investmentTypes.filter(t => PHYSICAL_TYPES.includes(t));
    return { ...effectiveFilters, investmentTypes: types.length > 0 ? types : [...PHYSICAL_TYPES] };
  }, [effectiveFilters]);

  const financialFilters = useMemo<FilterState>(() => {
    const types = effectiveFilters.investmentTypes.length === 0
      ? [...FINANCIAL_TYPES]
      : effectiveFilters.investmentTypes.filter(t => FINANCIAL_TYPES.includes(t));
    return { ...effectiveFilters, investmentTypes: types.length > 0 ? types : [...FINANCIAL_TYPES] };
  }, [effectiveFilters]);

  // Chart level the bar chart is currently rendering: L1 = no drill, L2 / L3
  // when one or two steps have been drilled into.
  const industryChartLevel = (industryDrillPath.length + 1) as 1 | 2 | 3;

  // API arg: when drilled, ask the server to filter the industry CTE to the
  // deepest step's parent and group one level below it. The country/time-
  // series sections of the response stay unaffected.
  const industryDrillApiArg = useMemo<{ level: 1 | 2; label: string } | undefined>(() => {
    const last = industryDrillPath[industryDrillPath.length - 1];
    return last ? { level: last.level, label: last.label } : undefined;
  }, [industryDrillPath]);

  // Industry drill narrowing — passed alongside `filters.industries` rather
  // than merged into it. The user's panel selection in `filters.industries`
  // keeps its OR semantics across levels; the drill is a separate dimension
  // that AND-joins via the backend's `industriesNarrow` param. Otherwise a
  // drill into L1:C beside a panel pick (L3:26.1 …) would OR up to "anything
  // in C", erasing the narrower selection. (Spotlights are a separate AND
  // condition via `filters.spotlight`, so drills narrow them naturally.)
  const industriesNarrowArg = useMemo<string[] | undefined>(() => {
    if (industryLeaf) return industryLeaf.originals.map(l => `L${industryLeaf.level}:${l}`);
    const last = industryDrillPath[industryDrillPath.length - 1];
    return last ? [`L${last.level}:${last.label}`] : undefined;
  }, [industryLeaf, industryDrillPath]);

  // When a Wahlkreis is selected we narrow the bar / time-series charts to
  // its cluster ids without narrowing the embedded map points — the polygon
  // highlight is enough on the map. Country and aggregate-bubble selections
  // intentionally leave the charts full (today's behaviour), so we only
  // forward the IDs when `selectedWahlkreis` is set.
  const chartClusterIdsArg = useMemo<number[] | undefined>(
    () => selectedWahlkreis && aggregateClusterIds ? aggregateClusterIds : undefined,
    [selectedWahlkreis, aggregateClusterIds],
  );

  // Two data flows: chart+map (bundled) and tables (separate per tier)
  const { chartData, countryData, countryClusterIds, industryData, filterOptions, mapPoints: allPoints, maxVolume, loading: chartLoading } = useChartData(effectiveFilters, industryDrillApiArg, chartClusterIdsArg, industriesNarrowArg);

  // Ranked-chart inputs: normalise both aggregates to a uniform `label` field.
  const countryRanked = useMemo<RankedDatum[]>(
    () => countryData.map(b => ({ label: b.country, type: b.type, count: b.count, volume: b.volume })),
    [countryData],
  );
  const industryRanked = useMemo<RankedDatum[]>(
    () => industryData.map(b => ({ label: b.industry, type: b.type, count: b.count, volume: b.volume })),
    [industryData],
  );
  const points = useMemo(() => {
    let pts = allPoints;
    // Wahlkreis clicks also set `aggregateClusterIds` so the tables narrow,
    // but on the map we just want the polygon highlight — narrowing bubbles
    // too would blank everything outside that one Wahlkreis. Country and
    // aggregate-bubble clicks both clear `selectedWahlkreis`, so its presence
    // is a reliable signal that the IDs came from a Wahlkreis click.
    if (aggregateClusterIds != null && !selectedWahlkreis) {
      const idSet = new Set(aggregateClusterIds);
      pts = pts.filter(p => p.cluster_id != null && idSet.has(p.cluster_id));
    }
    // Industry narrowing: leaf overrides the drill step (it's strictly deeper
    // or at the same level). Use the matching MapPoint level field — L1 lives
    // on `primary_industry` for legacy reasons.
    const indNarrow: { level: 1 | 2 | 3; labels: string[] } | null = industryLeaf
      ? { level: industryLeaf.level, labels: industryLeaf.originals }
      : industryDrillPath.length > 0
        ? { level: industryDrillPath[industryDrillPath.length - 1]!.level, labels: [industryDrillPath[industryDrillPath.length - 1]!.label] }
        : null;
    if (indNarrow) {
      const set = new Set(indNarrow.labels);
      pts = pts.filter(p => {
        const v = indNarrow.level === 1 ? p.primary_industry
          : indNarrow.level === 2 ? p.industry_l2
          : p.industry_l3;
        return v != null && set.has(v);
      });
    }
    return pts;
  }, [allPoints, aggregateClusterIds, selectedWahlkreis, industryLeaf, industryDrillPath]);

  const { rows: physicalRows, totalCount: physicalTotalCount, page: physicalResolvedPage, loading: physicalLoading } =
    useTablePage(physicalFilters, physicalPage, PAGE_SIZE, physicalSort, selectedClusterId, filterChangeId, aggregateClusterIds, activeTiers.physical, industriesNarrowArg);
  const { rows: financialRows, totalCount: financialTotalCount, page: financialResolvedPage, loading: financialLoading } =
    useTablePage(financialFilters, financialPage, PAGE_SIZE, financialSort, selectedClusterId, filterChangeId, aggregateClusterIds, activeTiers.financial, industriesNarrowArg);

  // Sync resolved pages (when findCluster navigates to a different page)
  useEffect(() => {
    if (physicalResolvedPage !== physicalPage) setPhysicalPage(physicalResolvedPage);
  }, [physicalResolvedPage]);
  useEffect(() => {
    if (financialResolvedPage !== financialPage) setFinancialPage(financialResolvedPage);
  }, [financialResolvedPage]);

  // Record one page-view per mount (fire-and-forget). The ref guards against
  // React StrictMode's double-invoke in dev so we don't double-count.
  const pageviewSent = useRef(false);
  useEffect(() => {
    if (pageviewSent.current) return;
    pageviewSent.current = true;
    trackPageview();
  }, []);

  // Load meta + wahlkreise on mount. Germany states geojson is bundled into
  // the widget so the host page doesn't need to serve a static asset.
  useEffect(() => {
    Promise.all([
      fetchMeta(),
      fetchWahlkreise().catch(() => null),
    ]).then(([metaData, wkData]) => {
      setMeta(metaData);
      // A shared link's window wins over the data's own bounds — but clamped
      // into them: a link from six months ago can carry a date outside today's
      // range, which would put the slider off its track.
      const min = new Date(metaData.dateRange.min).getTime();
      const max = new Date(metaData.dateRange.max).getTime();
      const clamp = (ts: number) => Math.min(Math.max(ts, min), max);
      const urlFrom = initialUrl.current.dateFrom;
      const urlTo = initialUrl.current.dateTo;
      setFromTimestamp(urlFrom ? clamp(Date.parse(urlFrom)) : min);
      setToTimestamp(urlTo ? clamp(Date.parse(urlTo)) : max);
      setGeoJsonData(germanyStatesGeoJson as GeoJsonData);
      setWahlkreiseData(wkData);
    }).catch(err => {
      setInitError(err instanceof Error ? err.message : 'Failed to initialize');
    });
  }, []);

  // ─── Shareable URL ──────────────────────────────────────────────────────
  //
  // The widget writes its filters into the host page's query string so a view
  // can be sent to someone. Everything that makes this safe on a page we do
  // not own is in utils/urlState.ts; the three things that live here:
  //
  //   1. replaceState, never pushState. GA4's enhanced measurement (and
  //      Plausible's SPA script) count a pageview on pushState and popstate but
  //      not on replaceState — pushing per filter change would inflate this
  //      page's pageviews by an order of magnitude. It would also fill the
  //      host's back stack, so "back" would stop returning the reader to
  //      wherever they came from.
  //   2. history.state is passed straight back through, not replaced with
  //      null. InvestmentMap pushes { mapFullscreen: true } when the map goes
  //      fullscreen, and the map's legend can change filters while it is open;
  //      writing null there would erase that marker and the back gesture would
  //      leave the page instead of closing the map.
  //   3. The query string is merged, not replaced — see mergeOwnParams.
  const lastWrittenUrl = useRef<string | null>(null);
  // The write still waiting in the timer below. Flushed on unmount, so a
  // language switch (which remounts and re-reads the URL) never loses the last
  // filter change.
  const pendingUrlWrite = useRef<(() => void) | null>(null);
  useEffect(() => () => pendingUrlWrite.current?.(), []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Everything happens inside the timer, nothing outside it. A slider drag
    // re-runs this effect on every frame it renders; encoding there would
    // build a URLSearchParams per frame and throw all but the last away.
    // Trailing-only; a write still pending at unmount is flushed (above).
    const write = () => {
      pendingUrlWrite.current = null;
      const query = encodeUrlState({ filters, fromTimestamp, toTimestamp, minTimestamp, maxTimestamp });
      if (query === lastWrittenUrl.current) return;
      lastWrittenUrl.current = query;
      const merged = mergeOwnParams(window.location.search, query);
      // Nothing to do if the address already says this. Worth checking: on a
      // plain load the encoded view is empty and this would otherwise rewrite
      // the URL for no reason, re-encoding whatever host params it carries.
      if (toSearchString(merged) === window.location.search) return;
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + toSearchString(merged) + window.location.hash,
      );
    };
    pendingUrlWrite.current = write;
    const timer = setTimeout(write, 400);
    return () => clearTimeout(timer);
  }, [filters, fromTimestamp, toTimestamp, minTimestamp, maxTimestamp]);

  // Back/forward. We only ever replaceState, so these events come from the
  // map's fullscreen entry or from the host page's own navigation — but the
  // address can still differ from what we last wrote, and a stale view would
  // then disagree with the URL someone is looking at.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = (e: PopStateEvent) => {
      // InvestmentMap's entry. Its own handler owns this one.
      if ((e.state as { mapFullscreen?: boolean } | null)?.mapFullscreen) return;

      const incoming = window.location.search.replace(/^\?/, '');
      if (incoming === lastWrittenUrl.current) return;
      const next = decodeUrlState(incoming);
      // Record what we are applying BEFORE the state lands, so the write
      // effect above sees no change and stays quiet.
      lastWrittenUrl.current = encodeUrlState({
        filters: next.filters,
        fromTimestamp: next.dateFrom ? Date.parse(next.dateFrom) : minTimestamp,
        toTimestamp: next.dateTo ? Date.parse(next.dateTo) : maxTimestamp,
        minTimestamp,
        maxTimestamp,
      });
      setFilters(next.filters);
      if (minTimestamp > 0) {
        const clamp = (ts: number) => Math.min(Math.max(ts, minTimestamp), maxTimestamp);
        setFromTimestamp(next.dateFrom ? clamp(Date.parse(next.dateFrom)) : minTimestamp);
        setToTimestamp(next.dateTo ? clamp(Date.parse(next.dateTo)) : maxTimestamp);
      }
      setPhysicalPage(0);
      setFinancialPage(0);
      setFilterChangeId(c => c + 1);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [minTimestamp, maxTimestamp]);

  // schema.org Dataset markup for the host page. Emitted once, from /api/meta,
  // and never per filter — see utils/structuredData.ts for why, and for the
  // hand-off to a server-rendered copy.
  useEffect(() => {
    if (!meta) return;
    return injectDatasetJsonLd(meta, resolveCanonicalPageUrl(pageUrl), apiBaseUrl ?? getApiBase());
  }, [meta, pageUrl, apiBaseUrl]);

  // Country drill-down: clicking a country bar filters the table + map to the
  // clusters behind it (the bar chart precomputes the merged cluster ids,
  // including for the aggregated "Sonstige" bucket). Country isn't a filter
  // dimension, so this is the only way to scope to it.
  // Clearing the industry drill is a single operation across the two pieces
  // of state (path + leaf), so collapse it into one helper to avoid drift.
  const clearIndustryDrill = useCallback(() => {
    setIndustryDrillPath([]);
    setIndustryLeaf(null);
  }, []);

  // Country and Wahlkreis share `aggregateClusterIds`, so they remain mutually
  // exclusive — picking one replaces the other. The industry drill is a
  // separate dimension and now composes with whichever location filter is
  // active (the API takes a Wahlkreis-derived `chartClusterIds` and the
  // industry drill arg side by side, and the tables narrow by both).
  const handleCountrySelect = useCallback((label: string, ids: number[]) => {
    setSelectedCountry(prev => {
      if (prev === label) { setAggregateClusterIds(null); return null; }
      setAggregateClusterIds(ids.length > 0 ? ids : null);
      setPhysicalPage(0);
      setFinancialPage(0);
      return label;
    });
    setSelectedWahlkreis(null);
  }, []);

  // Filter-panel and date-slider changes leave chart-click selections in
  // place — a Wahlkreis / country / industry drill is an independent
  // dimension that composes with the filter set, so the user shouldn't
  // lose their selection when they narrow the date range or pick a state.
  // "Clear all" and the chip × still wipe everything.
  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    setPhysicalPage(0);
    setFinancialPage(0);
    setSelectedClusterId(null);
    setFilterChangeId(c => c + 1);
  }, []);

  const handleTimeChange = useCallback((from: number, to: number) => {
    setFromTimestamp(from);
    setToTimestamp(to);
    setPhysicalPage(0);
    setFinancialPage(0);
    setSelectedClusterId(null);
    setFilterChangeId(c => c + 1);
  }, []);

  const handleClearAll = useCallback(() => {
    setFilters(defaultFilters);
    setActiveTiers({ physical: true, financial: false });
    if (meta) {
      setFromTimestamp(new Date(meta.dateRange.min).getTime());
      setToTimestamp(new Date(meta.dateRange.max).getTime());
    }
    setSelectedClusterId(null);
    setAggregateClusterIds(null);
    setSelectedCountry(null);
    setSelectedWahlkreis(null);
    clearIndustryDrill();
    setPhysicalPage(0);
    setFinancialPage(0);
    mapRef.current?.fitBounds(GERMANY_BOUNDS);
  }, [meta, clearIndustryDrill]);

  const handleAggregateClick = useCallback((clusterIds: number[]) => {
    setAggregateClusterIds(prev => {
      // Toggle off if same set
      if (prev && prev.length === clusterIds.length && prev.every((id, i) => id === clusterIds[i])) {
        return null;
      }
      return clusterIds;
    });
    clearIndustryDrill();
    setSelectedCountry(null);
    setSelectedWahlkreis(null);
    setPhysicalPage(0);
    setFinancialPage(0);
  }, [clearIndustryDrill]);

  const handleWahlkreisClick = useCallback((wkrNr: number, name: string, clusterIds: number[]) => {
    setSelectedWahlkreis(prev => {
      if (prev?.wkrNr === wkrNr) {
        setAggregateClusterIds(null);
        return null;
      }
      setAggregateClusterIds(clusterIds.length > 0 ? clusterIds : null);
      setPhysicalPage(0);
      setFinancialPage(0);
      return { wkrNr, name };
    });
    setSelectedCountry(null);
  }, []);

  const handleClearAggregateFilter = useCallback(() => {
    setAggregateClusterIds(null);
    clearIndustryDrill();
    setSelectedCountry(null);
    setSelectedWahlkreis(null);
    setPhysicalPage(0);
    setFinancialPage(0);
  }, [clearIndustryDrill]);

  // Spotlight active-state. The filter itself is FilterState.spotlight (the
  // slug, matched against curated membership server-side); the card only
  // highlights while nothing else is narrowing the view — otherwise the
  // highlighted card would lie about what the user is seeing.
  const activeSpotlightId = useMemo(() => {
    if (!filters.spotlight) return null;
    const otherFiltersActive =
      filters.federalStates.length > 0 ||
      filters.investmentStatuses.length > 0 ||
      filters.industries.length > 0 ||
      filters.companies.length > 0 ||
      filters.search !== '' ||
      // Tier toggle changes investmentTypes — only the default set counts as "no narrowing".
      filters.investmentTypes.length !== DEFAULT_INVESTMENT_TYPES.length ||
      filters.investmentTypes.some(t => !DEFAULT_INVESTMENT_TYPES.includes(t)) ||
      selectedCountry !== null ||
      selectedWahlkreis !== null ||
      aggregateClusterIds !== null ||
      industryDrillPath.length > 0 ||
      industryLeaf !== null;
    return otherFiltersActive ? null : filters.spotlight;
  }, [filters, selectedCountry, selectedWahlkreis, aggregateClusterIds, industryDrillPath, industryLeaf]);

  // Spotlights are top-level "view presets": reset everything else so the
  // spotlight's scope is unambiguous, then set the spotlight filter.
  const handleSpotlightClick = useCallback((spotlight: Spotlight) => {
    setFilters({ ...defaultFilters, spotlight: spotlight.slug });
    setActiveTiers({ physical: true, financial: false });
    if (meta) {
      setFromTimestamp(new Date(meta.dateRange.min).getTime());
      setToTimestamp(new Date(meta.dateRange.max).getTime());
    }
    setSelectedClusterId(null);
    setAggregateClusterIds(null);
    setSelectedCountry(null);
    setSelectedWahlkreis(null);
    clearIndustryDrill();
    setPhysicalPage(0);
    setFinancialPage(0);
    setFilterChangeId(c => c + 1);
    mapRef.current?.fitBounds(GERMANY_BOUNDS);
  }, [meta, clearIndustryDrill]);

  const handleClearSpotlight = useCallback(() => {
    setFilters(prev => ({ ...prev, spotlight: '' }));
    setPhysicalPage(0);
    setFinancialPage(0);
    setFilterChangeId(c => c + 1);
  }, []);

  // Industry chart click. Two outcomes:
  //   1. Drill — clicked bar has 2+ children and we're still above L3 → push
  //      a new step; the chart refetches grouped one level deeper. The leaf
  //      highlight clears because the user moved on from this view.
  //   2. Leaf — anything else (single/zero child, L3, or the "Sonstige" tail,
  //      which is aggregate). Highlight + filter at the current chart level,
  //      no chart switch. Re-clicking the same leaf clears it.
  // Clicking always clears other chart-side drills (country / Wahlkreis).
  const handleIndustrySelect = useCallback((label: string, _ids: number[], originals: string[]) => {
    const chartLevel = (industryDrillPath.length + 1) as 1 | 2 | 3;
    // "Sonstige" merges many originals; never treat it as a drillable node.
    const isAggregate = originals.length !== 1 || originals[0] !== label;
    const children = isAggregate ? [] : getChildrenForClick(filterOptions.industryTree, industryDrillPath, label);
    // Drill as soon as the node has any deeper child — see drillableIndustryLabels.
    const canDrill = chartLevel < 3 && children.length > 0;

    if (canDrill) {
      setIndustryDrillPath(prev => [...prev, { level: chartLevel as 1 | 2, label }]);
      setIndustryLeaf(null);
    } else {
      setIndustryLeaf(prev =>
        prev && prev.level === chartLevel && prev.label === label
          ? null
          : { level: chartLevel, label, originals },
      );
    }
    // Don't clear Wahlkreis / country here — the industry drill is its own
    // dimension and composes with whatever location filter is already active.
    setPhysicalPage(0);
    setFinancialPage(0);
  }, [industryDrillPath, filterOptions.industryTree]);

  // Breadcrumb jump: truncate the drill path to `depth` steps (0 = top, no
  // drill). Always clears any leaf highlight — it belonged to the current
  // chart level, which we're now leaving.
  const popIndustryDrillTo = useCallback((depth: number) => {
    setIndustryDrillPath(prev => prev.slice(0, depth));
    setIndustryLeaf(null);
    setPhysicalPage(0);
    setFinancialPage(0);
  }, []);

  // Build the breadcrumb passed to the chart. Empty array = chart hides the
  // breadcrumb row entirely. The last segment has no onClick (we're already
  // viewing it).
  const industryBreadcrumb = useMemo(() => {
    if (industryDrillPath.length === 0) return [];
    return [
      { label: m.app.allIndustries, onClick: () => popIndustryDrillTo(0) },
      ...industryDrillPath.map((step, i) => ({
        label: step.label,
        onClick: i < industryDrillPath.length - 1 ? () => popIndustryDrillTo(i + 1) : undefined,
      })),
    ];
  }, [industryDrillPath, popIndustryDrillTo, m]);

  // Labels at the current chart level whose tree node has at least one deeper
  // child — i.e. clicking them will drill (vs. fall back to highlight + filter).
  // The chart uses this to render a chevron only on drillable rows.
  //
  // Rule used to require ≥ 2 children ("don't drill if only one bar would
  // appear"), but with a spotlight or panel filter active the tree can be
  // pre-narrowed to a single-child path (e.g. Verteidigung → only L3:30.3
  // has data → C has just L2:30 as a child). Insisting on ≥ 2 children
  // there meant the user couldn't drill at all. Allowing single-child drills
  // surfaces which sub-branch holds the filtered data and lets the user
  // continue to L3 from there. Empty at L3 (nothing deeper to drill into).
  const drillableIndustryLabels = useMemo<Set<string>>(() => {
    if (industryChartLevel >= 3) return new Set();
    let nodes: HierarchicalFilterOption[] = filterOptions.industryTree;
    for (const step of industryDrillPath) {
      const found = nodes.find(n => n.label === step.label && n.level === step.level);
      if (!found || !found.children) return new Set();
      nodes = found.children;
    }
    return new Set(nodes.filter(n => (n.children?.length ?? 0) > 0).map(n => n.label));
  }, [filterOptions.industryTree, industryDrillPath, industryChartLevel]);

  // Remove a single value from one of the chip-able filters. `search` is the
  // one non-array chip: removing it clears the whole term.
  const removeFilterValue = useCallback((key: keyof FilterState, value: string) => {
    if (key === 'search') {
      handleFiltersChange({ ...filters, search: '' });
      return;
    }
    const current = filters[key];
    if (Array.isArray(current)) {
      handleFiltersChange({ ...filters, [key]: current.filter(v => v !== value) });
    }
  }, [filters, handleFiltersChange]);

  const resetDate = useCallback(() => {
    handleTimeChange(minTimestamp, maxTimestamp);
  }, [handleTimeChange, minTimestamp, maxTimestamp]);

  // The range is "active" (worth a chip) once it's narrower than the full span.
  const dateActive = fromTimestamp - minTimestamp > DAY_MS || maxTimestamp - toTimestamp > DAY_MS;
  const fmtMonthYear = (ts: number) => new Date(ts).toLocaleDateString(getLocale(), { month: '2-digit', year: 'numeric' });
  const dateLabel = `${fmtMonthYear(fromTimestamp)} – ${fmtMonthYear(toTimestamp)}`;

  // The active chart-click drill, as a removable chip (only one is ever set).
  // For industry we show the deepest active label — the leaf when present,
  // else the deepest drill step.
  const deepestIndustryLabel = industryLeaf?.label
    ?? industryDrillPath[industryDrillPath.length - 1]?.label
    ?? null;
  const selectionChip = selectedWahlkreis ? { kind: m.app.selectionWahlkreis, label: selectedWahlkreis.name }
    : selectedCountry ? { kind: m.app.selectionCountry, label: otherLabel(selectedCountry) }
    : deepestIndustryLabel ? { kind: m.app.selectionIndustry, label: industryLabel(deepestIndustryLabel) }
    : null;
  // Display form of the same selection, for the table's banner.
  const selectionLabel = selectedWahlkreis?.name
    ?? (selectedCountry ? otherLabel(selectedCountry) : null)
    ?? (deepestIndustryLabel ? industryLabel(deepestIndustryLabel) : null)
    ?? undefined;

  const handleTypeFilterChange = useCallback((types: string[]) => {
    setFilters(prev => {
      const hadFinancial = prev.investmentTypes.length === 0 || FINANCIAL_TYPES.some(t => prev.investmentTypes.includes(t));
      const hasFinancial = types.length === 0 || FINANCIAL_TYPES.some(t => types.includes(t));
      if (!hadFinancial && hasFinancial) setBubbleMode('cluster');
      return { ...prev, investmentTypes: types };
    });
    setPhysicalPage(0);
    setFinancialPage(0);
    setFilterChangeId(c => c + 1);
  }, []);

  const handleBubbleClick = useCallback((point: MapPoint) => {
    // Clicking a bubble opens the same detail modal as a table row — no
    // auto-zoom. Fetch the cluster so the modal header and timeline are
    // complete; fall back to point-only data if there's no cluster or it fails.
    // Financial transactions stay anonymised — no detail modal for them.
    if (FINANCIAL_TYPES.includes(point.type_of_investment || '')) return;
    const cid = point.cluster_id;
    if (cid == null) {
      const a = announcementFromPoint(point);
      setMapModal({ announcement: a, entries: [a] });
      return;
    }
    fetchClusterAnnouncements(cid)
      .then(entries => {
        const match = entries.find(e => e.id === point.announcement_id) ?? entries[0] ?? announcementFromPoint(point);
        setMapModal({ announcement: match, entries: entries.length > 0 ? entries : [match] });
      })
      .catch(() => {
        const a = announcementFromPoint(point);
        setMapModal({ announcement: a, entries: [a] });
      });
  }, []);

  const handleNewsItemClick = useCallback((a: Announcement) => {
    // A banner item is already a full Announcement — only the cluster timeline
    // needs fetching for the modal; fall back to a single-entry modal.
    if (a.cluster_id == null) {
      setMapModal({ announcement: a, entries: [a] });
      return;
    }
    fetchClusterAnnouncements(a.cluster_id)
      .then(entries => {
        const match = entries.find(e => e.id === a.id) ?? entries[0] ?? a;
        setMapModal({ announcement: match, entries: entries.length > 0 ? entries : [a] });
      })
      .catch(() => setMapModal({ announcement: a, entries: [a] }));
  }, []);

  const handleRowClick = useCallback((clusterId: number) => {
    selectionSourceRef.current = 'table';
    setSelectedClusterId(prev => prev === clusterId ? null : clusterId);
  }, []);

  const handleModalClose = useCallback(() => {
    setSelectedClusterId(null);
  }, []);

  const handlePhysicalPageChange = useCallback((p: number) => {
    setPhysicalPage(p);
  }, []);
  const handleFinancialPageChange = useCallback((p: number) => {
    setFinancialPage(p);
  }, []);

  const handlePhysicalSortChange = useCallback((s: TableSort) => {
    setPhysicalSort(s);
    setPhysicalPage(0);
  }, []);
  const handleFinancialSortChange = useCallback((s: TableSort) => {
    setFinancialSort(s);
    setFinancialPage(0);
  }, []);

  if (initError) {
    return (
      <Container className="dz-investments-widget-root color-data">
        <ErrorMessage>{initError}</ErrorMessage>
      </Container>
    );
  }

  if (!meta) {
    return (
      <LoadingContainer
        className="dz-investments-widget-root color-data"
        $fullDataset={isFullDatasetView(initialUrl.current)}
      >
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      </LoadingContainer>
    );
  }

  // Rendered into one of two slots depending on viewport (see GruppierungDesktopSlot).
  const gruppierungToggle = (
    <BubbleModeGroup>
      <ToggleLabel>{m.app.grouping}</ToggleLabel>
      <ToggleGroup>
        <ToggleButton active={bubbleMode === 'off'} onClick={() => setBubbleMode('off')}>
          {m.app.groupingNone}
        </ToggleButton>
        <ToggleButton active={bubbleMode === 'cluster'} onClick={() => setBubbleMode('cluster')}>
          {m.app.groupingCluster}
        </ToggleButton>
        <ToggleButton active={bubbleMode === 'single'} onClick={() => setBubbleMode('single')}>
          {m.app.groupingSites}
        </ToggleButton>
      </ToggleGroup>
    </BubbleModeGroup>
  );

  return (
    <Container className="dz-investments-widget-root color-data">
      <Spotlights
        activeSpotlightId={activeSpotlightId}
        onSpotlightClick={handleSpotlightClick}
        onClearSpotlight={handleClearSpotlight}
      />
      <DashboardGrid>
        {/* Publication dates, then the news band, then the filters — the band
            heads the dashboard, with its provenance line directly above it.
            First publication is a fixed fact about the tracker; the update date
            comes from the data (newest pipeline run behind a live announcement)
            and is dropped rather than faked if the API doesn't report one. */}
        <PublicationDates>
          {m.app.firstPublished}
          {meta.lastUpdated ? m.app.lastUpdate(formatDateLong(meta.lastUpdated)) : ''}
        </PublicationDates>

        {/* Slim news band above the filter block.
            effectiveFilters (not filters) so it also follows the time slider;
            clusterIds/industriesNarrow mirror what the tables get, so country
            / Wahlkreis / bubble selections and industry drills apply. */}
        <LatestNews
          filters={effectiveFilters}
          clusterIds={aggregateClusterIds}
          industriesNarrow={industriesNarrowArg}
          onItemClick={handleNewsItemClick}
        />

        <FilterPanel
          filters={filters}
          filterOptions={filterOptions}
          minTimestamp={minTimestamp}
          maxTimestamp={maxTimestamp}
          fromTimestamp={fromTimestamp}
          toTimestamp={toTimestamp}
          onFiltersChange={handleFiltersChange}
          onTimeChange={handleTimeChange}
          onClearAll={handleClearAll}
        />

        <ActiveFilterChips
          filters={filters}
          dateActive={dateActive}
          dateLabel={dateLabel}
          onResetDate={resetDate}
          onRemoveValue={removeFilterValue}
          selection={selectionChip}
          onClearSelection={handleClearAggregateFilter}
          onClearAll={handleClearAll}
        />

        <ChartViewToggle>
          <ToggleRowGroup>
            <ToggleGroup>
              <ToggleButton
                active={activeTiers.physical}
                data-icon-before="factory"
                onClick={() => {
                  const next = toggleTier(filters.investmentTypes, 'physical');
                  const nextState = deriveTierState(next);
                  setActiveTiers({ physical: nextState.physicalOn, financial: nextState.financialOn });
                  if (!nextState.physicalOn && nextState.financialOn) setTableView('financial');
                  handleTypeFilterChange(next);
                }}
              >
                {m.tiers.physical}
              </ToggleButton>
              <ToggleButton
                active={activeTiers.financial}
                data-icon-before="trending_up"
                onClick={() => {
                  const next = toggleTier(filters.investmentTypes, 'financial');
                  const nextState = deriveTierState(next);
                  setActiveTiers({ physical: nextState.physicalOn, financial: nextState.financialOn });
                  if (!nextState.financialOn && nextState.physicalOn) setTableView('physical');
                  handleTypeFilterChange(next);
                }}
              >
                {m.tiers.financial}
              </ToggleButton>
            </ToggleGroup>
            <ToggleDivider aria-hidden="true" />
            <BubbleModeGroup>
              <ToggleLabel>{m.app.visualisation}</ToggleLabel>
              <ToggleGroup>
              <ToggleButton active={chartView === 'industries'} onClick={() => setChartView('industries')}>
                {m.app.industries}
              </ToggleButton>
              <ToggleButton active={chartView === 'countries'} onClick={() => setChartView('countries')}>
                {m.app.countries}
              </ToggleButton>
              <ToggleButton active={chartView === 'timeseries'} onClick={() => setChartView('timeseries')}>
                {m.app.timeline}
              </ToggleButton>
              </ToggleGroup>
            </BubbleModeGroup>
          </ToggleRowGroup>
          <GruppierungDesktopSlot>{gruppierungToggle}</GruppierungDesktopSlot>
        </ChartViewToggle>

        <ChartAndMapRow>
          {chartView === 'industries' ? (
            <RankedBarChart
              title={m.app.industries}
              data={industryRanked}
              clusterIds={{}}
              // Highlight only when the leaf belongs to the level the chart
              // is currently showing — drill steps live in the breadcrumb,
              // not as a highlighted bar (we're viewing their children).
              selected={industryLeaf && industryLeaf.level === industryChartLevel ? industryLeaf.label : null}
              onSelect={handleIndustrySelect}
              investmentTypeFilter={filters.investmentTypes}
              activeTiers={activeTiers}
              onTypeFilterChange={handleTypeFilterChange}
              breadcrumb={industryBreadcrumb}
              drillableLabels={drillableIndustryLabels}
              formatLabel={industryLabel}
              // Show the first-encounter caption only when there's actually
              // something to drill into and the user hasn't started yet.
              hint={industryDrillPath.length === 0 && drillableIndustryLabels.size > 0
                ? m.app.drillHint
                : undefined}
            />
          ) : chartView === 'countries' ? (
            <RankedBarChart
              title={m.app.countries}
              data={countryRanked}
              clusterIds={countryClusterIds}
              selected={selectedCountry}
              onSelect={handleCountrySelect}
              investmentTypeFilter={filters.investmentTypes}
              activeTiers={activeTiers}
              onTypeFilterChange={handleTypeFilterChange}
            />
          ) : (
            <TimeSeriesChart
              chartData={chartData}
              investmentTypeFilter={filters.investmentTypes}
              activeTiers={activeTiers}
              onTypeFilterChange={handleTypeFilterChange}
            />
          )}
          <GruppierungMobileSlot>{gruppierungToggle}</GruppierungMobileSlot>
          <InvestmentMap
            points={points}
            maxVolume={maxVolume}
            bubbleMode={bubbleMode}
            geoJsonData={geoJsonData}
            wahlkreiseData={wahlkreiseData}
            selectedClusterId={selectedClusterId}
            selectionSourceRef={selectionSourceRef}
            aggregateClusterIds={aggregateClusterIds}
            selectedWahlkreis={selectedWahlkreis}
            investmentTypeFilter={filters.investmentTypes}
            activeTiers={activeTiers}
            availableTypes={filterOptions.investmentTypes}
            onBubbleClick={handleBubbleClick}
            onAggregateClick={handleAggregateClick}
            onWahlkreisClick={handleWahlkreisClick}
            onTypeFilterChange={handleTypeFilterChange}
            mapRef={mapRef}
          />
        </ChartAndMapRow>

      {/* Tables live inside DashboardGrid so the sticky FilterCard's
          containing block spans them and it stays pinned while they scroll. */}
      {(() => {
        const showPhysical = activeTiers.physical && (!activeTiers.financial || tableView === 'physical');
        const showFinancial = activeTiers.financial && (!activeTiers.physical || tableView === 'financial');
        const bothActive = activeTiers.physical && activeTiers.financial;

        const tableToggle = bothActive ? (
          <ToggleGroup>
            <ToggleButton active={tableView === 'physical'} onClick={() => setTableView('physical')}>
              {m.tiers.physical}
            </ToggleButton>
            <ToggleButton active={tableView === 'financial'} onClick={() => setTableView('financial')}>
              {m.tiers.financial}
            </ToggleButton>
          </ToggleGroup>
        ) : undefined;

        return (
          <>
            {showPhysical && (
              physicalLoading && physicalRows.length === 0 ? (
                // No taller reservation here on purpose: TableContainer caps
                // the table at max-height 500px (InvestmentTable.tsx), so the
                // settled block is ~567px and the shared 400px box is already
                // close. Reserving more would overshoot on every load.
                <LoadingOverlay>
                  <Spinner />
                </LoadingOverlay>
              ) : (
                <div style={{ opacity: physicalLoading ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
                  <InvestmentTable
                    mode="physical"
                    rows={physicalRows}
                    totalCount={physicalTotalCount}
                    page={physicalPage}
                    pageSize={PAGE_SIZE}
                    sort={physicalSort}
                    selectedClusterId={selectedClusterId}
                    aggregateFilterActive={aggregateClusterIds != null || (industriesNarrowArg?.length ?? 0) > 0}
                    aggregateFilterLabel={selectionLabel}
                    onClearAggregateFilter={handleClearAggregateFilter}
                    onRowClick={handleRowClick}
                    onModalClose={handleModalClose}
                    onPageChange={handlePhysicalPageChange}
                    onSortChange={handlePhysicalSortChange}
                    headerRight={(
                      <>
                        {tableToggle}
                        <TableSearchSlot>
                          <SearchInput
                            value={filters.search}
                            onChange={v => handleFiltersChange({ ...filters, search: v })}
                          />
                        </TableSearchSlot>
                      </>
                    )}
                    onExport={() => setShowExportDialog(true)}
                  />
                </div>
              )
            )}

            {showFinancial && (
              financialLoading && financialRows.length === 0 ? (
                // No taller reservation here on purpose: TableContainer caps
                // the table at max-height 500px (InvestmentTable.tsx), so the
                // settled block is ~567px and the shared 400px box is already
                // close. Reserving more would overshoot on every load.
                <LoadingOverlay>
                  <Spinner />
                </LoadingOverlay>
              ) : (
                <div style={{ opacity: financialLoading ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
                  <InvestmentTable
                    mode="financial"
                    rows={financialRows}
                    totalCount={financialTotalCount}
                    page={financialPage}
                    pageSize={PAGE_SIZE}
                    sort={financialSort}
                    selectedClusterId={selectedClusterId}
                    aggregateFilterActive={aggregateClusterIds != null || (industriesNarrowArg?.length ?? 0) > 0}
                    aggregateFilterLabel={selectionLabel}
                    onClearAggregateFilter={handleClearAggregateFilter}
                    onRowClick={handleRowClick}
                    onModalClose={handleModalClose}
                    onPageChange={handleFinancialPageChange}
                    onSortChange={handleFinancialSortChange}
                    headerRight={tableToggle}
                  />
                </div>
              )
            )}
          </>
        );
      })()}
      </DashboardGrid>

      <FooterRemark>
        {/* Dates moved to the top of the dashboard (see PublicationDates); the
            footer keeps the disclaimer and the contact line. */}
        <FooterText>
          {m.app.disclaimer}
        </FooterText>
        <FooterContact>
          {m.app.missingProject}{' '}
          <FooterSuggestButton onClick={() => setShowSuggestDialog(true)}>
            {m.app.suggestProject}
          </FooterSuggestButton>
        </FooterContact>
        <FooterContact>
          {m.app.contact}{' '}
          <a href="mailto:alexander.marx@dezernatzukunft.org">alexander.marx@dezernatzukunft.org</a>
        </FooterContact>
      </FooterRemark>

      {mapModal && (
        <AnnouncementModal
          announcement={mapModal.announcement}
          preloadedEntries={mapModal.entries}
          onClose={() => setMapModal(null)}
        />
      )}

      {showSuggestDialog && (
        <FeedbackDialog
          mode="new_project"
          onClose={() => setShowSuggestDialog(false)}
        />
      )}

      {showExportDialog && (
        <ExportDialog
          filters={physicalFilters}
          clusterIds={aggregateClusterIds}
          industriesNarrow={industriesNarrowArg}
          totalEntryCount={physicalTotalCount}
          onClose={() => setShowExportDialog(false)}
        />
      )}
    </Container>
  );
}
